'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { LocaleChip } from '@/components/common/locale-chip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  LOCALE_CHIP_LABELS,
  SUGGESTED_LOCALE_SET,
  SUGGESTED_LOCALES,
} from '@/i18n/locale-chip-labels';
import { type AppLocale, SUPPORTED_LOCALES } from '@/i18n/locales';

export type LanguagePickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeLocale: AppLocale;
  isPending: boolean;
  onSelect: (locale: AppLocale) => void;
};

export function LanguagePickerDialog({
  open,
  onOpenChange,
  activeLocale,
  isPending,
  onSelect,
}: LanguagePickerDialogProps) {
  const t = useTranslations('UserMenu');

  const availableLocales = useMemo(
    () =>
      SUPPORTED_LOCALES.filter((l) => !SUGGESTED_LOCALE_SET.has(l)).sort((a, b) =>
        LOCALE_CHIP_LABELS[a].native.localeCompare(LOCALE_CHIP_LABELS[b].native),
      ),
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl'>
        <DialogHeader className='shrink-0 space-y-0 border-b px-6 pb-4 pt-6 text-start pe-14'>
          <DialogTitle>{t('changeLanguageTitle')}</DialogTitle>
          <DialogDescription className='sr-only'>{t('languageMenu')}</DialogDescription>
        </DialogHeader>
        <div className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5'>
          <section aria-labelledby='language-picker-suggested-heading'>
            <h2
              id='language-picker-suggested-heading'
              className='mb-3 text-sm font-semibold text-foreground'
            >
              {t('suggestedLanguages')}
            </h2>
            <div className='grid grid-cols-2 gap-3'>
              {SUGGESTED_LOCALES.map((code) => (
                <LocaleChip
                  key={code}
                  code={code}
                  selected={activeLocale === code}
                  disabled={isPending}
                  onSelect={() => onSelect(code)}
                />
              ))}
            </div>
          </section>

          <section className='mt-8' aria-labelledby='language-picker-available-heading'>
            <h2
              id='language-picker-available-heading'
              className='mb-3 text-sm font-semibold text-foreground'
            >
              {t('availableLanguages')}
            </h2>
            <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
              {availableLocales.map((code) => (
                <LocaleChip
                  key={code}
                  code={code}
                  selected={activeLocale === code}
                  disabled={isPending}
                  onSelect={() => onSelect(code)}
                />
              ))}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
